import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/chillnn/cn";
import { getDiffDaysByDay } from "@/lib/dayjs/get-diff-days-by-day";
import { getTodaysDate } from "@/lib/dayjs/get-todays-date";

import { BlankContentForList } from "@/components/features/_components";

import { Table, TableBody } from "@/components/ui/table/table";

import { PlanRateMatrixDailyTableHeader } from "./header";
import { PlanRateMatrixPlanHeaderRow } from "./plan-header-row";
import { PlanRateMatrixDailyTableRow } from "./row";
import { groupRowsByPlanID } from "../../../model/group-rows-by-plan-id";

import type { PlanRoomDailyRateRow } from "../../../model/types";

/** 左列（プラン/部屋名）。w-64 = 256px */
const NAME_COLUMN_WIDTH = 256;
/** 日次列。在庫テーブルの w-20 = 80px に合わせる */
const DAILY_COLUMN_WIDTH = 80;
const TABLE_ROOT_ID = "plan-rate-matrix-table-root";

type HoverTarget = {
	rowId: string;
	date: string;
};

type Props = {
	hotelID: string;
	yearMonth: string;
	dates: string[];
	rows: PlanRoomDailyRateRow[];
	adultNum: number;
};

/**
 * 操作月が今日の月と一致する場合、今日の日付まで横スクロールする（在庫テーブルと同様）。
 * 人数切替で Suspense 再マウントした直後も sticky が見た目ズレしないよう、レイアウト後に再適用する。
 */
const useAutoHorizontalScroll = (yearMonth: string, canShowRows: boolean, layoutKey: string) => {
	useEffect(() => {
		if (!canShowRows) {
			return;
		}

		const tableRootElement = document.getElementById(TABLE_ROOT_ID);
		if (!tableRootElement) {
			return;
		}

		const today = getTodaysDate();
		const [year, month] = yearMonth.split("-");
		const targetLeft =
			today.slice(0, 7) === yearMonth
				? DAILY_COLUMN_WIDTH * getDiffDaysByDay(today, `${year}-${month}-01`)
				: 0;

		const applyScroll = () => {
			// jsdom は scrollTo 未実装。ブラウザでも scrollLeft 直指定で十分
			tableRootElement.scrollLeft = targetLeft;
		};

		applyScroll();
		// 人数切替直後はテーブル幅確定前に sticky が描画されることがあるため、次フレームでもう一度合わせる
		let nestedRafId = 0;
		const rafId = requestAnimationFrame(() => {
			applyScroll();
			nestedRafId = requestAnimationFrame(applyScroll);
		});

		return () => {
			cancelAnimationFrame(rafId);
			cancelAnimationFrame(nestedRafId);
		};
	}, [canShowRows, layoutKey, yearMonth]);
};

export const PlanRateMatrixDailyRateTable = memo(({ hotelID, yearMonth, dates, rows, adultNum }: Props) => {
	const canShowRows = rows.length > 0;
	// 人数・列数が変わるとテーブル実幅と sticky の前提が変わる
	const layoutKey = `${adultNum}:${dates.length}:${rows.length}`;
	useAutoHorizontalScroll(yearMonth, canShowRows, layoutKey);
	const today = getTodaysDate();
	const groupedRows = groupRowsByPlanID(rows);
	const [hoverTarget, setHoverTarget] = useState<HoverTarget | null>(null);

	const tableWidthPx = useMemo(
		() => NAME_COLUMN_WIDTH + dates.length * DAILY_COLUMN_WIDTH,
		[dates.length],
	);

	const handleHoverCell = useCallback((next: HoverTarget | null) => {
		setHoverTarget(next);
	}, []);

	const handleMouseLeaveTable = useCallback(() => {
		setHoverTarget(null);
	}, []);

	// 空表示は <table> 外に置く。div を table 配下に入れると validateDOMNesting 警告になる
	if (!canShowRows) {
		return (
			<BlankContentForList
				content={
					"表示できる料金行がありません。\nプラン・部屋料金ランク・フィルタ条件を確認してください。"
				}
			/>
		);
	}

	return (
		<Table
			id={TABLE_ROOT_ID}
			className={cn(
				"relative table-fixed border-separate border-spacing-0",
				// Table 既定の min-w-[min-content] を打ち消す。人数切替でセル文言幅が変わっても列幅を固定する
				"min-w-0",
			)}
			style={{ width: tableWidthPx }}
			tableWrapperClassName="max-h-[800px]"
			onMouseLeave={handleMouseLeaveTable}
		>
			<colgroup>
				<col style={{ width: NAME_COLUMN_WIDTH }} />
				{dates.map((date) => (
					<col key={date} style={{ width: DAILY_COLUMN_WIDTH }} />
				))}
			</colgroup>
			<PlanRateMatrixDailyTableHeader
				yearMonth={yearMonth}
				dates={dates}
				today={today}
				highlightedDate={hoverTarget?.date ?? null}
			/>
			<TableBody>
				{groupedRows.flatMap((row) => {
					const planHeaderRowId = `plan-header-${row.planID}`;
					const room = (
						<PlanRateMatrixDailyTableRow
							key={row.id}
							hotelID={hotelID}
							row={row}
							dates={dates}
							adultNum={adultNum}
							today={today}
							isRowHighlighted={hoverTarget?.rowId === row.id}
							highlightedDate={hoverTarget?.date ?? null}
							onHoverCell={handleHoverCell}
						/>
					);
					// プラン見出しは料金なしの独立行。先頭部屋のセルをプラン代表料金に見せない
					if (!row.showPlanHeading) {
						return [room];
					}
					return [
						<PlanRateMatrixPlanHeaderRow
							key={planHeaderRowId}
							hotelID={hotelID}
							row={row}
							dates={dates}
							today={today}
							isRowHighlighted={hoverTarget?.rowId === planHeaderRowId}
							highlightedDate={hoverTarget?.date ?? null}
							onHoverCell={handleHoverCell}
						/>,
						room,
					];
				})}
			</TableBody>
		</Table>
	);
});

PlanRateMatrixDailyRateTable.displayName = "PlanRateMatrixDailyRateTable";
